import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { ScheduledJobs_ListResponseDados } from '../contracts/ScheduledJobs_ListResponse'
import type { ScheduledJobs_JobRow } from '../contracts/ScheduledJobs_JobRow'
import type { ScheduledJobs_SaveInput } from '../contracts/ScheduledJobs_SaveInput'
import type { ScheduledJobs_ToggleInput } from '../contracts/ScheduledJobs_ToggleInput'
import type { ScheduledJobs_RunNowInput } from '../contracts/ScheduledJobs_RunNowInput'
import type { ScheduledJobs_HistoryResponseDados } from '../contracts/ScheduledJobs_HistoryResponse'
import type { ScheduledJobs_RunRow } from '../contracts/ScheduledJobs_RunRow'
import type { ScheduledJobs_HeartbeatResponseDados } from '../contracts/ScheduledJobs_HeartbeatResponse'
// Import side-effects: registers the contracts with the responseContract registry.
import '../contracts/ScheduledJobs_ListResponse'
import '../contracts/ScheduledJobs_JobRow'
import '../contracts/ScheduledJobs_SaveInput'
import '../contracts/ScheduledJobs_ToggleInput'
import '../contracts/ScheduledJobs_RunNowInput'
import '../contracts/ScheduledJobs_HistoryResponse'
import '../contracts/ScheduledJobs_RunRow'
import '../contracts/ScheduledJobs_HeartbeatResponse'

/**
 * useScheduledJobs — SOLE network owner for the S10 Full-only console's
 * `scheduled-jobs/*` routes (app-api-service).
 *
 * All requests go to `config.api.sistemaHost`. The `no_httpservice_in_vue`
 * qa rule forbids any fetch in .vue files — this composable is the single
 * gateway.
 *
 * Call `_reset()` in afterEach for test isolation.
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const jobRows = ref<ScheduledJobs_JobRow[]>([])
const historyRows = ref<ScheduledJobs_RunRow[]>([])
const heartbeat = ref<ScheduledJobs_HeartbeatResponseDados | null>(null)

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

/**
 * save/toggle both echo the single mutated row via ScheduledJobs_ListResponse
 * (see contract docblock) — replace it in place if already present, else
 * append. Keeps jobRows the single source of truth the table renders from,
 * so a toggle's own switch reflects the CONFIRMED state, never an optimistic
 * guess that would silently revert on failure.
 */
function upsertRow(row: ScheduledJobs_JobRow): void {
  const idx = jobRows.value.findIndex((r) => r.id === row.id)
  if (idx === -1) {
    jobRows.value = [...jobRows.value, row]
  } else {
    jobRows.value = [...jobRows.value.slice(0, idx), row, ...jobRows.value.slice(idx + 1)]
  }
}

// ── Public composable ────────────────────────────────────────────────────────

export function useScheduledJobs() {
  /**
   * listJobs — GET scheduled-jobs (full roster, enabled + disabled).
   */
  async function listJobs(): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl('scheduled-jobs'),
        { responseContract: 'ScheduledJobs_ListResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: ScheduledJobs_ListResponseDados }
      jobRows.value = resp.dados.rows
      log('useScheduledJobs', 'listJobs', 'Jobs carregados', { count: jobRows.value.length })
    } catch (err) {
      log('useScheduledJobs', 'listJobs', 'Erro ao carregar jobs agendados', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * saveJob — POST scheduled-jobs/save. `input.id` null/absent = create.
   * Binds EXACTLY the ScheduledJobs_SaveInput field names.
   */
  async function saveJob(input: ScheduledJobs_SaveInput): Promise<ScheduledJobs_JobRow> {
    const body: ScheduledJobs_SaveInput = {
      name: input.name,
      job_type: input.job_type,
      schedule_kind: input.schedule_kind,
      channel: input.channel,
      enabled: input.enabled ?? false,
      max_attempts: input.max_attempts ?? 3,
      ...(input.id != null ? { id: input.id } : {}),
      ...(input.schedule_time != null ? { schedule_time: input.schedule_time } : {}),
      ...(input.interval_minutes != null ? { interval_minutes: input.interval_minutes } : {}),
      ...(input.params != null ? { params: input.params } : {}),
      ...(input.channel_target != null ? { channel_target: input.channel_target } : {}),
    }
    const raw = await httpService.post(
      sistemaUrl('scheduled-jobs/save'),
      body,
      { inputContract: 'ScheduledJobs_SaveInput', responseContract: 'ScheduledJobs_ListResponse', skipErrorDisplay: false },
    )
    // O backend responde HTTP 200 mesmo na RECUSA (name_already_used /
    // job_not_found) — sendResponse/sendError não mexem no status — então o
    // httpService não lança sozinho. Ler o `success` de DENTRO do envelope é
    // obrigatório aqui; ver useGraceAdmin.ts:84-87 / skill billing-staff-console §4.
    const resp = raw as { success?: boolean; dados: ScheduledJobs_ListResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'scheduled_job_save_failed')
    }
    const row = resp.dados.rows[0]
    upsertRow(row)
    log('useScheduledJobs', 'saveJob', 'Job salvo', { id: row.id, name: row.name })
    return row
  }

  /**
   * toggleJob — POST scheduled-jobs/toggle. Quick enable/disable flip.
   */
  async function toggleJob(id: number, enabled: boolean): Promise<ScheduledJobs_JobRow> {
    const body: ScheduledJobs_ToggleInput = { id, enabled }
    const raw = await httpService.post(
      sistemaUrl('scheduled-jobs/toggle'),
      body,
      { inputContract: 'ScheduledJobs_ToggleInput', responseContract: 'ScheduledJobs_ListResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: ScheduledJobs_ListResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'scheduled_job_toggle_failed')
    }
    const row = resp.dados.rows[0]
    upsertRow(row)
    log('useScheduledJobs', 'toggleJob', 'Job alternado', { id: row.id, enabled: row.enabled })
    return row
  }

  /**
   * runNow — POST scheduled-jobs/run-now. Executes the job's job_type
   * executor immediately, through the SAME lease/overlap/attempts/dead-letter
   * machinery a real tick uses.
   *
   * The envelope's `success` flag only guards ACCEPTANCE of the request
   * (job_not_found / job_already_running). The run's OWN outcome
   * (success/failed/skipped/dead) lives inside the returned row's `status` /
   * `error_message` — read by the CALLER, never turned into a thrown error
   * here. See ScheduledJobs_RunRow contract docblock.
   */
  async function runNow(id: number): Promise<ScheduledJobs_RunRow> {
    const body: ScheduledJobs_RunNowInput = { id }
    const raw = await httpService.post(
      sistemaUrl('scheduled-jobs/run-now'),
      body,
      { inputContract: 'ScheduledJobs_RunNowInput', responseContract: 'ScheduledJobs_HistoryResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: ScheduledJobs_HistoryResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'scheduled_job_run_now_failed')
    }
    const row = resp.dados.rows[0]
    log('useScheduledJobs', 'runNow', 'Execução manual concluída', { id, status: row.status })
    return row
  }

  /**
   * loadHistory — GET scheduled-jobs/{id}/history (up to 50 rows, newest first).
   */
  async function loadHistory(jobId: number): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl(`scheduled-jobs/${jobId}/history`),
        { responseContract: 'ScheduledJobs_HistoryResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: ScheduledJobs_HistoryResponseDados }
      historyRows.value = resp.dados.rows
      log('useScheduledJobs', 'loadHistory', 'Histórico carregado', { jobId, count: historyRows.value.length })
    } catch (err) {
      log('useScheduledJobs', 'loadHistory', 'Erro ao carregar histórico', {
        jobId,
        message: (err as Error)?.message,
      })
      throw err
    }
  }

  /**
   * loadHeartbeat — GET scheduled-jobs/heartbeat. The DISPATCHER-level
   * proof-of-life the console banner reads (armadilha 5 — the S4.4 cron sat
   * inert for weeks and nothing distinguished that from "nothing due").
   */
  async function loadHeartbeat(): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl('scheduled-jobs/heartbeat'),
        { responseContract: 'ScheduledJobs_HeartbeatResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: ScheduledJobs_HeartbeatResponseDados }
      heartbeat.value = resp.dados
      log('useScheduledJobs', 'loadHeartbeat', 'Batimento do despachante carregado', {
        everTicked: heartbeat.value.ever_ticked,
        isStale: heartbeat.value.is_stale,
      })
    } catch (err) {
      log('useScheduledJobs', 'loadHeartbeat', 'Erro ao carregar batimento do despachante', {
        message: (err as Error)?.message,
      })
      throw err
    }
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    jobRows.value = []
    historyRows.value = []
    heartbeat.value = null
  }

  return {
    // reactive state
    jobRows,
    historyRows,
    heartbeat,
    // methods
    listJobs,
    saveJob,
    toggleJob,
    runNow,
    loadHistory,
    loadHeartbeat,
    _reset,
  }
}
